import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { Routes } from '@share/constants';
import { RootState } from '@share/utils';
import { GetWalletNumber } from '@share/utils';
import { RewardWalletPriority } from '@share/common-types';
import { getNextRewardByPriority } from '@utils';

import { WalletRewardReadyCard } from '../../common/wallet-reward-ready-card';
import { WalletRewardProgressCompact } from '../../common/wallet-reward-progress-compact';

import './style.scss';

const NEXT_AVAILABLE_PRIORITIES: (RewardWalletPriority.Highest | RewardWalletPriority.Cheapest)[] = [
  RewardWalletPriority.Highest,
  RewardWalletPriority.Cheapest,
];

function isNextAvailablePriority(p: RewardWalletPriority | undefined): p is RewardWalletPriority.Highest | RewardWalletPriority.Cheapest {
  return p === RewardWalletPriority.Highest || p === RewardWalletPriority.Cheapest;
}

interface IProps {
  onSeeAllClick?: () => void;
}

export function HeaderWalletRewardMessage(props: IProps) {
  const { onSeeAllClick } = props;

  const loginStore = useSelector((state: RootState) => state.loginStore);
  const { account, user, userWallet, userWalletReward } = loginStore;

  const enableWalletReward = account?.enableWalletReward;
  const isLoggedAshevillePlay = account?.isAshevillePlay && !user?.isAshevillePlayAdmin;
  const isLogged = !!userWallet || isLoggedAshevillePlay;

  if (!enableWalletReward || !isLogged) {
    return null;
  }

  const priority = account?.rewardWalletPriority;
  const allRewards = userWalletReward?.allRewards ?? [];
  const nextAvailableReward = userWalletReward?.nextAvailableReward;
  const showNextAvailable = isNextAvailablePriority(priority);
  const nextReward = showNextAvailable
    ? getNextRewardByPriority(allRewards, priority, nextAvailableReward)
    : undefined;

  const accumulated = Number(userWalletReward?.accumulatedProgress ?? 0);
  const threshold = Number(userWalletReward?.threshold ?? 0);
  const hasProgress = threshold > 0;
  const totalAvailable = userWalletReward?.totalAvailableRewards ?? 0;
  const searchHref = account?.name ? `/${account.name}${Routes.Search}` : null;

  return (
    <div className="header-wallet-reward-message">
      {/* 1. Reward ready to use (first in hierarchy) */}
      {nextReward != null && (
        <WalletRewardReadyCard
          reward={nextReward}
          account={account}
          searchHref={searchHref}
          compact
        />
      )}

      {/* 2. Progress toward next reward */}
      {hasProgress ? (
        <WalletRewardProgressCompact
          accumulated={accumulated}
          threshold={threshold}
          showMicrocopy
        />
      ) : (
        <>
          {totalAvailable > 0 && (
            <p className="header-wallet-reward-message__rewards-available">
              <FormattedMessage id="wallet.header.rewardsAvailable" values={{ count: GetWalletNumber(totalAvailable, account) }} />
            </p>
          )}
          {!account?.walletIsExternal && (userWalletReward?.pendingProgress ?? 0) > 0 && (
            <p className="header-wallet-reward-message__collected">
              <FormattedMessage
                id="wallet.header.progressCollected"
                values={{ count: GetWalletNumber(userWalletReward?.pendingProgress as number, account) }}
              />
            </p>
          )}
        </>
      )}

      {/* 3. See all rewards */}
      <p className="header-wallet-reward-message__see-all-wrap">
        <Link
          to={`/${account?.name}${Routes.Rewards}`}
          className="header-wallet-reward-message__see-all"
          onClick={onSeeAllClick}
        >
          <FormattedMessage id="wallet.header.seeAllRewards" />
        </Link>
      </p>
    </div>
  );
}
